/**
 * The slice of a project solution that ties a channel to it: a channel's
 * `botId` is the start-chat bot id, which a solution carries as
 * `botSolutionId`.
 */
export interface BotSolutionRef {
  botSolutionId?: string | null;
}

/** Answers "which solution owns this channel's `botId`", or `null`. */
export type SolutionResolver<T extends BotSolutionRef> = (
  botId: string | null | undefined
) => T | null;

/**
 * The ONE definition of channel → solution, shared by the "Channels Using It"
 * grouping and by `useGoToChannel`, so a channel's box and the page its pill
 * opens cannot name two different solutions.
 *
 * - Several solutions with the same `botSolutionId`: the FIRST one in list
 *   order wins (what `Array.prototype.find` returned before this existed).
 * - An empty / absent `botId` resolves to `null`. Only solutions with a
 *   non-empty `botSolutionId` enter the index, so an absent id never meets a
 *   solution whose own id is absent too (`undefined === undefined`).
 *
 * The index is built once per call of this factory; build it once per
 * `solutions` list (e.g. in a `useMemo`) and call the returned function per
 * channel.
 */
export const createSolutionResolver = <T extends BotSolutionRef>(
  solutions: readonly T[] | null | undefined
): SolutionResolver<T> => {
  const byBotId = new Map<string, T>();
  for (const solution of solutions ?? []) {
    const key = solution.botSolutionId;
    if (key && !byBotId.has(key)) byBotId.set(key, solution);
  }
  // `''` is never a key (the guard above), so an absent `botId` misses.
  return (botId) => byBotId.get(botId ?? '') ?? null;
};

/**
 * `createSolutionResolver`, or `null` while the list itself is unknown
 * (`ctx.solutions` starts `undefined` and is reset to it after a solution is
 * deleted). The distinction is what keeps "we do not know the solutions yet"
 * from being drawn as "this channel has no solution": a resolver over an
 * absent list answers `null` for every channel, exactly as one over a list
 * that lacks the channel's solution does. An EMPTY list is known, and gets a
 * resolver.
 */
export const createLoadedSolutionResolver = <T extends BotSolutionRef>(
  solutions: readonly T[] | null | undefined
): SolutionResolver<T> | null =>
  solutions == null ? null : createSolutionResolver(solutions);

/** One-off form of `createSolutionResolver`, for a single lookup. */
export const resolveSolutionByBotId = <T extends BotSolutionRef>(
  solutions: readonly T[] | null | undefined,
  botId: string | null | undefined
): T | null => createSolutionResolver(solutions)(botId);
